<template>
  <div class="event-files">
    <ul class="event-files-list list-unstyled mb-2" v-if="files.length > 0">
      <li
        v-for="file in files"
        :key="file.id"
        class="d-flex align-items-center mb-1"
      >
        <i class="fas fa-paperclip mr-2"></i>
        <a :href="fileUrl(file)" target="_blank" rel="noopener">
          {{ file.name }}
        </a>
        <small class="text-muted ml-2">{{ file.human_readable_size }}</small>
        <a
          v-if="canManage"
          href="#"
          class="text-danger ml-auto"
          :title="$t('FILES.DELETE_FILE')"
          @click.prevent="deleteFile(file)"
        >
          <i class="fas fa-trash"></i>
        </a>
      </li>
    </ul>

    <dropzone-file-upload
      v-if="canManage"
      :key="renderKey"
      :url="uploadUrl"
      multiple
      @success="attachFile"
      @error="onUploadError"
      @queuecomplete="onQueueComplete"
    />
  </div>
</template>

<script>
import swal from "sweetalert2";
import DropzoneFileUpload from "@/components/Inputs/DropzoneFileUpload";
import { buildFileUrl } from "@/helpers/fileUrl";

const apiUrl = process.env.VUE_APP_API_BASE_URL;

export default {
  name: "event-files",

  components: {
    DropzoneFileUpload,
  },

  props: {
    event: {
      type: Object,
      required: true,
    },
    canManage: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      renderKey: 0,
      pendingAttachments: [],
    };
  },

  computed: {
    files() {
      return this.event.files || [];
    },

    uploadUrl() {
      return `${apiUrl}/uploads/events/${this.event.id}/files`;
    },
  },

  methods: {
    fileUrl(file) {
      return buildFileUrl(file.url, file.name);
    },

    /**
     * Chaque fichier envoyé est rattaché à l'évènement dès la fin de son envoi.
     * Pas de restriction propre : la visibilité suit celle de l'évènement.
     */
    attachFile(file) {
      const me = this.$store.getters["profile/me"];
      const fileData = {
        type: "files",
        name: file.upload.filename,
        path: JSON.parse(file.xhr.response).path,
        excerpt: "",
        relationshipNames: ["organization", "object", "allowedLocations"],
        organization: {
          type: "organizations",
          id: this.event.organization.id,
        },
        object: {
          type: "events",
          id: this.event.id,
        },
        allowedLocations: me.restrict_to_locations
          ? me.allowedLocations.map((item) => ({
              type: "locations",
              id: item.id,
            }))
          : [],
      };

      this.pendingAttachments.push(
        this.$store
          .dispatch("files/add", fileData)
          .then(() => true)
          .catch(() => {
            this.$notify({
              type: "danger",
              message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
            });
            return false;
          })
      );
    },

    async onQueueComplete() {
      const attached = await Promise.all(this.pendingAttachments);
      this.pendingAttachments = [];
      if (attached.some(Boolean)) {
        this.$notify({
          type: "success",
          message: this.$t("FILES.FILE_ADDED"),
        });
      }
      this.renderKey = this.renderKey + 1;
      this.$emit("filesChanged");
    },

    async onUploadError(file) {
      const errorMessage =
        file.xhr?.response ||
        file.xhr?.statusText ||
        this.$t("ERRORS.FILE_UPLOAD_ERROR");
      await swal.fire({
        title: this.$t("ERRORS.FILE_UPLOAD_ERROR"),
        html: errorMessage,
        icon: "error",
        confirmButtonText: this.$t("COMMON.OK"),
        confirmButtonClass: "btn btn-primary",
      });
    },

    async deleteFile(file) {
      const confirmation = await swal.fire({
        title: this.$t("FILES.DELETE_THIS_FILE"),
        text: file.name,
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });
      if (confirmation.value !== true) {
        return;
      }

      try {
        await this.$store.dispatch("files/destroy", file.id);
        this.$notify({
          type: "success",
          message: this.$t("FILES.FILE_DELETED"),
        });
        this.$emit("filesChanged");
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },
  },
};
</script>
